import Papa from 'papaparse';

export const LABELS = {
  exact: 'Exact Match',
  combined: 'Combined Match',
  suspected: 'Suspected · different location',
  review: 'Needs Review',
  missingInventory: 'Missing from Expected',
  missingReshoot: 'Missing from Captured'
};

export const norm = value => String(value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
export const quantity = value => {
  const text = String(value ?? '').trim().replace(/,/g, '');
  return text && /^\d+(\.\d+)?$/.test(text) && Number.isFinite(Number(text)) ? Number(text) : null;
};
export const newId = () => window.crypto.randomUUID();
const sorted = ids => [...ids].sort();
const unique = values => [...new Set(values)];
const equalQuantity = (a, b) => a !== null && b !== null && Math.abs(a - b) <= Number.EPSILON * 8 * Math.max(1, Math.abs(a), Math.abs(b));
export const total = rows => rows.some(row => quantity(row.quantity) === null)
  ? null : rows.reduce((sum, row) => sum + quantity(row.quantity), 0);
export const quantityAgrees = (left, right) => equalQuantity(total(left), total(right));
export const safeImage = value => {
  if (typeof value !== 'string') return '';
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
  } catch {
    return '';
  }
};
export const locationLabel = row => row.path && row.path !== row.location
  ? `${row.location || 'Unknown'} (${row.path})` : row.location || '';
const valueAt = (data, aliases) => {
  const keys = Object.keys(data || {}).reverse();
  for (const alias of aliases) {
    const key = keys.find(candidate => norm(candidate) === norm(alias));
    if (key !== undefined && data[key] !== null && data[key] !== undefined && data[key] !== '') return data[key];
  }
  return '';
};
const PRIMARY_FIELDS = ['mpn', 'Manufacturer Part Number', 'Manufacturer PN', 'MFR Part Number', 'MFG Part Number', 'Part Number', 'Part No', 'Part Num', 'PN', 'SKU'];
const SECONDARY_FIELDS = ['secondarypartnumber', 'Secondary PN', 'Secondary Part No', 'SPN'];
const INTERNAL_FIELDS = ['ipn', 'Internal Part Number', 'Internal PN', 'Internal Part No'];
export const isPartIdentifierField = field => {
  const key = norm(field);
  return ['SKU', 'STOCKKEEPINGUNIT', 'ITEMNUMBER', 'ITEMNO', 'ITEMCODE', 'PRODUCTCODE', 'CATALOGNUMBER', 'CATALOGNO', 'SPN', 'SECONDARY', 'ALIASES'].includes(key)
    || /(?:PART(?:NUMBERS?|NOS?|NUMS?)|(?:MPN|IPN|CPN|PN)S?)(?:\d+)?$/.test(key);
};
const identifierValues = value => {
  if (Array.isArray(value)) return value.flatMap(identifierValues);
  if (typeof value !== 'string' && (typeof value !== 'number' || !Number.isFinite(value))) return [];
  // Do not split on spaces, hyphens, or slashes, which can be part of a real PN.
  return String(value).split(/[,;|\r\n]+/).map(item => item.trim())
    .filter(item => norm(item) && !/^(?:N\/A|NONE|NULL|UNKNOWN|NOT AVAILABLE|NOT APPLICABLE)$/i.test(item));
};
export function identifiersFrom(...records) {
  const fields = new Map();
  // Later values override the same source column, including explicit clears.
  for (const record of records) {
    for (const [field, value] of Object.entries(record || {})) {
      if (isPartIdentifierField(field)) fields.set(norm(field), value);
    }
  }
  const values = new Map();
  for (const value of fields.values()) {
    for (const identifier of identifierValues(value)) {
      if (!values.has(norm(identifier))) values.set(norm(identifier), identifier);
    }
  }
  return [...values.values()];
}
export const partIdentifiers = row => identifiersFrom({
  pn: row.pn, secondary: row.secondary, ipn: row.ipn, aliases: row.identifiers || []
});
export const primaryIdentifier = row => identifierValues(row.pn)[0] || identifierValues(row.ipn)[0]
  || identifierValues(row.secondary)[0] || partIdentifiers(row)[0] || '';
const partNumbers = row => partIdentifiers(row).map(norm);
const identifierExtension = (row, covered) => {
  const additional = partNumbers(row).filter(identifier => !covered.has(identifier));
  return additional.length ? [sorted(additional)] : [];
};
const shareGroupIdentifier = rows => {
  if (!rows.length) return false;
  let shared = new Set(partNumbers(rows[0]));
  for (const row of rows.slice(1)) {
    const numbers = new Set(partNumbers(row));
    shared = new Set([...shared].filter(number => numbers.has(number)));
    if (!shared.size) return false;
  }
  return shared.size > 0;
};

export function expectedRow(item) {
  const data = { ...item.sourceRow, ...item.partData };
  return {
    id: item.lineItemId,
    pn: valueAt(data, PRIMARY_FIELDS),
    secondary: valueAt(data, SECONDARY_FIELDS),
    ipn: valueAt(data, INTERNAL_FIELDS),
    identifiers: identifiersFrom(item.sourceRow, item.partData),
    quantity: valueAt(data, ['quantity', 'In Stock', 'QTY']),
    manufacturer: valueAt(data, ['manufacturer', 'MFR', 'MFG']),
    datecode: valueAt(data, ['datecode', 'DC']),
    location: valueAt(data, ['location', 'Final Location']),
    path: valueAt(data, ['Location Tree']),
    images: [],
    source: item.sourceRow || data,
    item,
    created: Boolean(item.auditCreatedFrom)
  };
}

export function capturedRows(parts, details = {}) {
  const content = value => {
    if (typeof value === 'string') {
      try { value = JSON.parse(value); } catch { return {}; }
    }
    return value && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(Object.entries(value).map(([key, item]) => [norm(key).toLowerCase(), item])) : {};
  };
  return parts.map(original => {
    const part = { ...original, ...content(original.generatedContent), ...content(original.generatedIpnContent), ...content(original.manualContent) };
    // Physical box location matches the Reshoot export; proposed "New Location" stays a source field.
    const location = details.boxNames?.[part.boxId] || part.boxName || part.finalLocation || '';
    const path = details.locationTree?.[part.boxId] || part.locationTree || '';
    const imageValues = Array.isArray(part.images)
      ? part.images.map(image => typeof image === 'string' ? image : image.uri || image.url || image.imageUri || image.imageUrl)
      : [];
    for (let i = 1; i <= 5; i += 1) imageValues.push(part[`image${i}`] || part[`Image ${i}`]);
    imageValues.push(part.imageUri, part.imageUrl);
    return {
      id: original.partId == null ? '' : String(original.partId), pn: valueAt(part, PRIMARY_FIELDS), secondary: valueAt(part, SECONDARY_FIELDS),
      ipn: valueAt(part, INTERNAL_FIELDS), identifiers: identifiersFrom(part),
      quantity: valueAt(part, ['quantity', 'QTY']), manufacturer: valueAt(part, ['manufacturer', 'MFR']),
      datecode: valueAt(part, ['datecode', 'DC']), location, path,
      images: unique(imageValues.map(safeImage).filter(Boolean)), source: part
    };
  });
}

export const blankAudit = () => ({ version: 1, allocations: [], suppressedLeft: [], suppressedRight: [], confirmed: [], notes: {} });

export function loadDocument(raw, right) {
  let parsed = raw || { lineItems: [] };
  // Older packing-slip processing sometimes stored JSON inside a JSON string.
  for (let i = 0; typeof parsed === 'string' && i < 3; i += 1) parsed = JSON.parse(parsed);
  if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.lineItems)) {
    throw new Error('Expected inventory is malformed. It has not been replaced.');
  }
  const ids = new Set();
  const lineItems = parsed.lineItems.map(item => {
    if (!item || typeof item !== 'object' || !item.partData || typeof item.partData !== 'object' || Array.isArray(item.partData)) {
      throw new Error('An expected inventory row has invalid part data.');
    }
    const lineItemId = String(item.lineItemId || newId());
    if (ids.has(lineItemId)) throw new Error('Duplicate expected row IDs. Resolve these before auditing.');
    ids.add(lineItemId);
    return { ...item, lineItemId };
  });
  const document = { ...parsed, lineItems };
  if (parsed.audit) {
    validateAudit(parsed.audit, lineItems.map(expectedRow), right);
    document.audit = parsed.audit;
  } else {
    const audit = blankAudit();
    const claimed = new Set();
    for (const item of lineItems) {
      const matches = unique((Array.isArray(item.matchedParts) ? item.matchedParts : []).map(String));
      const valid = matches.filter(id => right.some(row => row.id === id));
      if (valid.some(id => claimed.has(id))) throw new Error('Existing matches assign one captured part more than once. Resolve these before auditing.');
      valid.forEach(id => claimed.add(id));
      if (valid.length) audit.allocations.push({
        id: `legacy-${item.lineItemId}`, left: [item.lineItemId], right: valid,
        note: '', origin: 'legacy'
      });
    }
    document.audit = audit;
  }
  return document;
}

export function validateAudit(audit, left, right) {
  if (!audit || audit.version !== 1 || !Array.isArray(audit.allocations)
    || !Array.isArray(audit.suppressedLeft) || !Array.isArray(audit.suppressedRight)
    || !Array.isArray(audit.confirmed) || !audit.notes || typeof audit.notes !== 'object' || Array.isArray(audit.notes)) {
    throw new Error('Unsupported or invalid audit review format.');
  }
  const leftIds = new Set(left.map(row => row.id));
  const rightIds = new Set(right.map(row => row.id));
  const claimedLeft = new Set(), claimedRight = new Set(), groupIds = new Set();
  for (const allocation of audit.allocations) {
    if (!allocation || typeof allocation.id !== 'string' || groupIds.has(allocation.id)
      || !Array.isArray(allocation.left) || !allocation.left.length || !Array.isArray(allocation.right) || !allocation.right.length
      || typeof allocation.note !== 'string' || !['manual', 'legacy', 'created'].includes(allocation.origin)) {
      throw new Error('Invalid saved match.');
    }
    groupIds.add(allocation.id);
    for (const [members, known, claimed] of [[allocation.left, leftIds, claimedLeft], [allocation.right, rightIds, claimedRight]]) {
      for (const id of members) {
        if (typeof id !== 'string' || !known.has(id) || claimed.has(id)) {
          throw new Error('A saved match contains missing or multiply assigned rows. Restore the source data or use a matching backup.');
        }
        claimed.add(id);
      }
    }
  }
  for (const [ids, known] of [[audit.suppressedLeft, leftIds], [audit.suppressedRight, rightIds]]) {
    if (ids.some(id => typeof id !== 'string' || !known.has(id)) || unique(ids).length !== ids.length) throw new Error('Invalid unmatch suppression.');
  }
  if (audit.confirmed.some(key => typeof key !== 'string') || Object.values(audit.notes).some(note => typeof note !== 'string')) {
    throw new Error('Invalid review confirmation or notes.');
  }
}

export const locationMatches = (left, right) => {
  const location = norm(left.location);
  const segments = String(right.path || '').split('>').map(norm);
  return Boolean(location && (location === norm(right.location) || segments.includes(location)
    || segments.some((_, index) => segments.slice(index).join('') === location)));
};
export const groupKey = group => JSON.stringify([sorted(group.left), sorted(group.right)]);
export const confirmationKey = group => JSON.stringify([
  group.status, Boolean(group.manual), sorted(group.left), sorted(group.right),
  ...[group.leftRows, group.rightRows].map(rows => [...rows].sort((a, b) => a.id.localeCompare(b.id))
    .map(row => [row.id, row.pn, row.secondary, row.quantity, row.location, row.path,
      ...identifierExtension(row, new Set([norm(row.pn), norm(row.secondary)]))]))
]);

export function analyze(left, right, audit = blankAudit()) {
  const groups = [], usedLeft = new Set(), usedRight = new Set();
  const leftMap = new Map(left.map(row => [row.id, row])), rightMap = new Map(right.map(row => [row.id, row]));
  const add = (leftIds, rightIds, status, reason, extra = {}) => {
    const group = {
      id: `auto-${JSON.stringify([sorted(leftIds), sorted(rightIds)])}`,
      left: leftIds, right: rightIds, status, reason,
      leftRows: leftIds.map(id => leftMap.get(id)), rightRows: rightIds.map(id => rightMap.get(id)), ...extra
    };
    group.leftQuantity = total(group.leftRows); group.rightQuantity = total(group.rightRows);
    group.note = group.note ?? audit.notes[groupKey(group)] ?? '';
    group.confirmed = audit.confirmed.includes(confirmationKey(group));
    groups.push(group);
    leftIds.forEach(id => usedLeft.add(id)); rightIds.forEach(id => usedRight.add(id));
  };
  // Saved allocations always take precedence over fresh automatic analysis.
  for (const allocation of audit.allocations) {
    add(allocation.left, allocation.right, allocation.left.length === 1 && allocation.right.length === 1 ? 'exact' : 'combined',
      'Explicit saved allocation. Group totals do not imply individual pack allocations.',
      { ...allocation, manual: true, created: allocation.origin === 'created' });
  }
  const suppressedLeft = new Set(audit.suppressedLeft), suppressedRight = new Set(audit.suppressedRight);
  const availableLeft = left.filter(row => !usedLeft.has(row.id) && !suppressedLeft.has(row.id));
  const availableRight = right.filter(row => !usedRight.has(row.id) && !suppressedRight.has(row.id));
  const index = new Map(), reverse = new Map(), candidates = new Map();
  for (const row of availableRight) {
    for (const pn of partNumbers(row)) {
      if (!index.has(pn)) index.set(pn, []);
      index.get(pn).push(row.id);
    }
  }
  for (const row of availableLeft) {
    const ids = unique(partNumbers(row).flatMap(pn => index.get(pn) || []));
    candidates.set(row.id, ids);
    for (const id of ids) {
      if (!reverse.has(id)) reverse.set(id, []);
      reverse.get(id).push(row.id);
    }
  }
  const exactEdges = new Map(), claims = new Map();
  for (const row of availableLeft) {
    const ids = candidates.get(row.id).filter(id => locationMatches(row, rightMap.get(id))
      && equalQuantity(quantity(row.quantity), quantity(rightMap.get(id).quantity)));
    exactEdges.set(row.id, ids);
    ids.forEach(id => claims.set(id, (claims.get(id) || 0) + 1));
  }
  for (const row of availableLeft) {
    const ids = exactEdges.get(row.id);
    if (ids.length === 1 && claims.get(ids[0]) === 1) add([row.id], ids, 'exact', 'Unique part number, quantity, and location match.');
  }
  const components = local => {
    const seenLeft = new Set(), seenRight = new Set(), result = [];
    for (const row of availableLeft) {
      if (usedLeft.has(row.id) || seenLeft.has(row.id)) continue;
      const leftIds = [], rightIds = [], queue = [row.id];
      seenLeft.add(row.id);
      while (queue.length) {
        const id = queue.pop(); leftIds.push(id);
        for (const rightId of candidates.get(id)) {
          if (usedRight.has(rightId) || (local && !locationMatches(leftMap.get(id), rightMap.get(rightId)))) continue;
          if (!seenRight.has(rightId)) {
            seenRight.add(rightId); rightIds.push(rightId);
            for (const next of reverse.get(rightId) || []) {
              if (!usedLeft.has(next) && !seenLeft.has(next) && (!local || locationMatches(leftMap.get(next), rightMap.get(rightId)))) {
                seenLeft.add(next); queue.push(next);
              }
            }
          }
        }
      }
      if (rightIds.length) result.push([leftIds, rightIds]);
    }
    return result;
  };
  const coherent = (ls, rs) => {
    if (!shareGroupIdentifier([...ls, ...rs])) return false;
    // Repeated final names under different paths must not be combined accidentally.
    if (new Set(rs.map(row => norm(row.path || row.location))).size === 1) return true;
    const locations = unique(ls.map(row => norm(row.location)));
    if (locations.length !== 1) return false;
    const scopes = rs.map(row => {
      const segments = String(row.path).split('>').map(norm), index = segments.indexOf(locations[0]);
      return index < 0 ? '' : segments.slice(0, index + 1).join('>');
    });
    return scopes.every(Boolean) && new Set(scopes).size === 1;
  };
  for (const [ls, rs] of components(true)) {
    const leftRows = ls.map(id => leftMap.get(id)), rightRows = rs.map(id => rightMap.get(id));
    if (quantityAgrees(leftRows, rightRows) && coherent(leftRows, rightRows)) {
      add(ls, rs, 'combined', `${ls.length} expected and ${rs.length} captured rows match by part, location, and total quantity. No individual allocation is implied.`);
    }
  }
  for (const [ls, rs] of components(false)) {
    const leftRows = ls.map(id => leftMap.get(id)), rightRows = rs.map(id => rightMap.get(id));
    const suspected = shareGroupIdentifier([...leftRows, ...rightRows]) && quantityAgrees(leftRows, rightRows)
      && leftRows.every(row => norm(row.location)) && rightRows.every(row => norm(row.path || row.location))
      && !leftRows.some(l => rightRows.some(r => locationMatches(l, r)));
    add(ls, rs, suspected ? 'suspected' : 'review', suspected
      ? 'Part number and total quantity agree at different locations. Review before accepting.'
      : 'Related parts found; quantities, locations, or ambiguous aliases require review.');
  }
  const allRightNumbers = new Set(right.flatMap(partNumbers)), allLeftNumbers = new Set(left.flatMap(partNumbers));
  for (const row of left) {
    if (!usedLeft.has(row.id)) {
      const problems = [];
      if (!partNumbers(row).length) problems.push('No part number identifier');
      if (quantity(row.quantity) === null) problems.push('Missing or invalid quantity');
      const related = suppressedLeft.has(row.id) || partNumbers(row).some(pn => allRightNumbers.has(pn));
      add([row.id], [], problems.length || related ? 'review' : 'missingReshoot',
        problems.length ? `${problems.join('; ')}. Source row retained for review.`
          : related ? 'Unmatched or related captured rows allocated elsewhere.' : 'Part number not found in captured inventory.');
    }
  }
  for (const row of right) {
    if (!usedRight.has(row.id)) {
      const related = suppressedRight.has(row.id) || partNumbers(row).some(pn => allLeftNumbers.has(pn));
      add([], [row.id], related ? 'review' : 'missingInventory', related ? 'Unmatched or related expected rows allocated elsewhere.' : 'Part number not found in expected inventory.');
    }
  }
  return groups;
}

export function matchRows(document, right, leftIds, rightIds, note = '', origin = 'manual', id = newId()) {
  if (!leftIds.length || !rightIds.length) throw new Error('Select at least one row on each side.');
  if (document.audit.allocations.some(group => group.left.some(row => leftIds.includes(row)) || group.right.some(row => rightIds.includes(row)))) {
    throw new Error('Unmatch the existing manual allocation before reassigning these rows.');
  }
  const invalidConfirmations = new Set(analyze(document.lineItems.map(expectedRow), right, document.audit)
    .filter(group => group.left.some(row => leftIds.includes(row)) || group.right.some(row => rightIds.includes(row)))
    .map(confirmationKey));
  const audit = { ...document.audit,
    confirmed: document.audit.confirmed.filter(key => !invalidConfirmations.has(key)),
    allocations: [...document.audit.allocations, {
      id, left: sorted(leftIds), right: sorted(rightIds), note, origin
    }]
  };
  validateAudit(audit, document.lineItems.map(expectedRow), right);
  return { ...document, audit };
}

export function unmatchGroup(document, group) {
  return { ...document, audit: {
    ...document.audit, allocations: document.audit.allocations.filter(allocation => allocation.id !== group.id),
    suppressedLeft: unique([...document.audit.suppressedLeft, ...group.left]),
    suppressedRight: unique([...document.audit.suppressedRight, ...group.right]),
    confirmed: document.audit.confirmed.filter(key => key !== confirmationKey(group))
  } };
}

export function toggleConfirmed(document, group) {
  const key = confirmationKey(group), confirmed = new Set(document.audit.confirmed);
  if (confirmed.has(key)) confirmed.delete(key); else confirmed.add(key);
  return { ...document, audit: { ...document.audit, confirmed: [...confirmed] } };
}

export function setGroupNote(document, group, note) {
  const allocation = document.audit.allocations.find(item => item.id === group.id);
  return { ...document, audit: {
    ...document.audit,
    allocations: allocation ? document.audit.allocations.map(item => item.id === group.id ? { ...item, note } : item) : document.audit.allocations,
    notes: { ...document.audit.notes, [groupKey(group)]: note }
  } };
}

export function createExpected(document, right, selectedIds, note = '') {
  const selected = right.filter(row => selectedIds.includes(row.id));
  if (!selected.length || selected.length !== selectedIds.length) throw new Error('Select valid captured rows.');
  if (selected.some(row => document.lineItems.some(item => item.auditCreatedFrom === row.id))) throw new Error('Expected inventory has already been created from a selected row.');
  if (document.audit.allocations.some(group => group.right.some(id => selectedIds.includes(id)))) throw new Error('Unmatch the selected manual allocation before creating expected rows.');
  let next = document;
  for (const row of selected) {
    const lineItemId = newId();
    const item = {
      lineItemId, auditCreatedFrom: row.id, partData: {
        mpn: primaryIdentifier(row), secondarypartnumber: row.secondary, aliases: partIdentifiers(row), quantity: row.quantity,
        manufacturer: row.manufacturer, datecode: row.datecode, location: row.location, ipn: row.ipn,
        coo: row.source.coo || '', rohsstatus: row.source.rohsstatus || ''
      }
    };
    next = { ...next, lineItems: [...next.lineItems, item] };
    next = matchRows(next, right, [lineItemId], [row.id], note, 'created');
  }
  return next;
}

export function savePayload(document, groups) {
  const byLeft = new Map();
  groups.filter(group => ['exact', 'combined'].includes(group.status)).forEach(group => group.left.forEach(id => byLeft.set(id, group)));
  // Keep legacy consumers usable for 1:N only; never pretend N:M has individual allocations.
  const validConfirmations = new Set(groups.map(confirmationKey));
  return { ...document, audit: { ...document.audit, confirmed: document.audit.confirmed.filter(key => validConfirmations.has(key)) },
    lineItems: document.lineItems.map(item => {
      const group = byLeft.get(item.lineItemId);
      return {
        ...item, matchedParts: group?.left.length === 1 ? group.right : [],
        totalActualQuantity: group?.left.length === 1 ? group.rightQuantity : 0
      };
    })
  };
}

export const emptyFilters = () => ({ pn: '', quantity: '', manufacturer: '', datecode: '', location: '', statuses: [] });
export const hasSideFilters = filter => Object.entries(filter).some(([key, value]) => key === 'statuses' ? value.length : value !== '');
export function rowMatches(row, group, filter) {
  return (!filter.pn || partNumbers(row).some(value => value.includes(norm(filter.pn))))
    && (!filter.quantity || (quantity(filter.quantity) !== null && equalQuantity(quantity(row.quantity), quantity(filter.quantity))))
    && (!filter.manufacturer || norm(row.manufacturer).includes(norm(filter.manufacturer)))
    && (!filter.datecode || norm(row.datecode).includes(norm(filter.datecode)))
    && (!filter.location || row.location === filter.location)
    && (!filter.statuses.length || filter.statuses.includes(group.manual ? 'exact' : group.status));
}
export const originMatches = (group, method) => !method || (method === 'manual' ? group.manual
  : method === 'automatic' ? !group.manual && ['exact', 'combined'].includes(group.status)
  : method === 'created' ? group.created : !['exact', 'combined'].includes(group.status));
export const reviewMatches = (group, review) => !review || (review === 'confirmed' ? group.confirmed : !group.confirmed);
export function sortGroups(groups, sort, direction = 'asc') {
  if (sort === 'source') return groups;
  const location = (group, side) => group[side].map(locationLabel).sort()[0] || '';
  const keys = sort === 'captured' ? ['rightRows'] : sort === 'both' ? ['leftRows', 'rightRows'] : ['leftRows'];
  return [...groups].sort((a, b) => {
    for (const key of keys) {
      const comparison = location(a, key).localeCompare(location(b, key), undefined, { numeric: true, sensitivity: 'base' });
      if (comparison) return direction === 'desc' ? -comparison : comparison;
    }
    return 0;
  });
}

export function parseExpectedCSV(text) {
  const result = Papa.parse(text.replace(/^\uFEFF/, ''), { header: true, skipEmptyLines: 'greedy', dynamicTyping: false });
  if (result.errors.length) throw new Error(`Invalid CSV: ${result.errors[0].message}`);
  if (!result.data.length) throw new Error('The CSV contains no inventory rows.');
  const lineItems = result.data.map(sourceRow => {
    const row = expectedRow({ lineItemId: newId(), partData: sourceRow });
    return {
      lineItemId: row.id, sourceRow,
      partData: { mpn: row.pn, secondarypartnumber: row.secondary, ipn: row.ipn, quantity: row.quantity, manufacturer: row.manufacturer, datecode: row.datecode, location: row.location }
    };
  });
  return { lineItems, audit: blankAudit() };
}

const canonical = value => {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])]));
  return value;
};
export const datasetSignature = (left, right) => JSON.stringify([left, right].map(rows => [...rows]
  .sort((a, b) => a.id.localeCompare(b.id))
  .map(row => [row.id, row.pn, row.secondary, row.quantity, row.manufacturer, row.datecode, row.location, row.path, canonical(row.source),
    ...identifierExtension(row, new Set([norm(row.pn), norm(row.secondary), ...identifiersFrom(row.source).map(norm)]))])));
export async function datasetFingerprint(left, right) {
  const bytes = new TextEncoder().encode(datasetSignature(left, right));
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}
export function restoreBackup(backup, projectId, fingerprint, document, right) {
  if (!backup || backup.version !== 1 || backup.projectId !== projectId || backup.fingerprint !== fingerprint) {
    throw new Error('This backup belongs to a different project or dataset.');
  }
  validateAudit(backup.audit, document.lineItems.map(expectedRow), right);
  return { ...document, audit: backup.audit };
}

const cellValue = value => value && typeof value === 'object' ? JSON.stringify(value) : value ?? '';
export function exportAudit(rows, groups, side) {
  const owners = new Map(groups.flatMap(group => group[side].map(id => [id, group])));
  const records = rows.map(row => {
    const group = owners.get(row.id);
    const others = side === 'left' ? group.rightRows : group.leftRows;
    const source = Object.fromEntries(Object.entries(row.source).map(([key, value]) => [key, cellValue(value)]));
    const supplement = side === 'left' && ['exact', 'combined'].includes(group.status)
      ? Object.fromEntries(['pn', 'secondary', 'quantity', 'manufacturer', 'datecode', 'location', 'path', 'ipn', 'images',
        'coo', 'rohsstatus', 'packaging', 'msl', 'serialnumber', 'lotcode', 'notes'].map(field => {
        // Keep one entry per source pack, including repeated quantities and blank values.
        const values = [...others].sort((a, b) => a.id.localeCompare(b.id))
          .map(other => cellValue(other[field] ?? other.source[field]));
        return [`Captured ${field}`, values.length > 1 ? JSON.stringify(values) : values[0] ?? ''];
      })) : {};
    return {
      ...source, ...supplement,
      'Audit Row ID': row.id, 'Audit Group': group.id, 'Audit Status': LABELS[group.status],
      'Audit Method': group.manual ? group.origin || 'manual' : 'automatic',
      'Audit Review': group.confirmed ? 'Confirmed' : 'Unconfirmed',
      'Audit Expected IDs': JSON.stringify(group.left), 'Audit Captured IDs': JSON.stringify(group.right),
      'Audit Expected Quantity': group.leftQuantity ?? '', 'Audit Captured Quantity': group.rightQuantity ?? '',
      'Audit Supplement Scope': group.left.length > 1 && group.right.length ? 'SHARED GROUP DATA — not individual allocation' : 'Individual expected row',
      'Audit Note': group.note, 'Audit Reason': group.reason,
      'Audit Inventory Origin': row.created ? 'Created from captured inventory' : 'Original'
    };
  });
  const fields = unique(records.flatMap(record => Object.keys(record)));
  return '\uFEFF' + Papa.unparse({ fields, data: records.map(record => fields.map(field => cellValue(record[field]))) }, {
    quotes: true, escapeFormulae: /^[\s]*[=+\-@\t\r]/
  });
}
